/**
 * Чтение параметров файла на клиенте до загрузки: длительность и разрешение
 * берём из <video>, размеры фото — из <img>. Так неподходящий файл
 * отклоняется мгновенно, без 800 мегабайт трафика.
 */
import { LIMITS } from '@reels/shared/constants';
import type { AssetKind } from '@reels/shared';
import { t } from '../i18n';

export interface MediaInfo {
  durationS: number | null;
  width: number | null;
  height: number | null;
}

export interface FileCheck {
  kind: AssetKind;
  info: MediaInfo;
  /** Сообщение на русском, если файл не подходит. */
  error: string | null;
}

const VIDEO_PROBE_TIMEOUT_MS = 20_000;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/jpg'];

export function isVideoFile(file: File): boolean {
  if (file.type.startsWith('video/')) return true;
  return /\.(mov|mp4|m4v|hevc|avi|mkv|webm)$/i.test(file.name);
}

export function isPhotoFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  return /\.(jpe?g|png|heic|heif)$/i.test(file.name);
}

export function detectKind(file: File, slot: 'reaction' | 'illustration'): AssetKind {
  if (slot === 'reaction') return 'reaction';
  return isVideoFile(file) ? 'video' : 'photo';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} ${t('video.unit.b')}`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} ${t('video.unit.kb')}`;
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} ${t('video.unit.mb')}`;
  return `${(mb / 1024).toFixed(1)} ${t('video.unit.gb')}`;
}

export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

/** Читает метаданные видео через скрытый <video> и объектный URL. */
export function readVideoInfo(file: File): Promise<MediaInfo> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve({ durationS: null, width: null, height: null });
      return;
    }
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    // iOS не грузит метаданные без этого атрибута.
    video.setAttribute('playsinline', '');

    let settled = false;
    const finish = (info: MediaInfo): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(info);
    };

    const timer = window.setTimeout(
      () => finish({ durationS: null, width: null, height: null }),
      VIDEO_PROBE_TIMEOUT_MS,
    );

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : null;
      finish({
        durationS: duration,
        width: video.videoWidth || null,
        height: video.videoHeight || null,
      });
    };
    video.onerror = () => finish({ durationS: null, width: null, height: null });
    video.src = url;
  });
}

export function readPhotoInfo(file: File): Promise<MediaInfo> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve({ durationS: null, width: null, height: null });
      return;
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    let settled = false;
    const finish = (info: MediaInfo): void => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve(info);
    };
    image.onload = () => finish({ durationS: null, width: image.naturalWidth, height: image.naturalHeight });
    // HEIC часто не рисуется в WebView — это не повод отклонять файл.
    image.onerror = () => finish({ durationS: null, width: null, height: null });
    image.src = url;
  });
}

export function readMediaInfo(file: File, kind: AssetKind): Promise<MediaInfo> {
  return kind === 'photo' ? readPhotoInfo(file) : readVideoInfo(file);
}

/** Проверка файла по LIMITS из @reels/shared. */
export function validate(file: File, kind: AssetKind, info: MediaInfo): string | null {
  if (kind === 'photo') {
    if (!isPhotoFile(file)) return t('video.error.not_photo');
    if (file.size > LIMITS.photo.maxBytes) {
      return t('video.error.photo_too_big', { max: formatBytes(LIMITS.photo.maxBytes) });
    }
    return null;
  }

  if (!isVideoFile(file)) return t('video.error.not_video');

  const limits = kind === 'reaction' ? LIMITS.reaction : LIMITS.illustrationVideo;
  if (file.size > limits.maxBytes) {
    return t('video.error.video_too_big', { size: formatBytes(file.size), max: formatBytes(limits.maxBytes) });
  }
  if (info.durationS !== null) {
    if (info.durationS > limits.maxDurationS) {
      return t('video.error.too_long', {
        duration: formatDuration(info.durationS),
        max: formatDuration(limits.maxDurationS),
      });
    }
    if (kind === 'reaction' && info.durationS < LIMITS.reaction.minDurationS) {
      return t('video.error.too_short', { n: LIMITS.reaction.minDurationS });
    }
  }
  return null;
}

/** Единая проверка: определить вид, прочитать метаданные, сверить с лимитами. */
export async function checkFile(file: File, slot: 'reaction' | 'illustration'): Promise<FileCheck> {
  const kind = detectKind(file, slot);
  if (slot === 'illustration' && !isVideoFile(file) && !isPhotoFile(file)) {
    return {
      kind,
      info: { durationS: null, width: null, height: null },
      error: t('video.error.unsupported'),
    };
  }
  const info = await readMediaInfo(file, kind);
  return { kind, info, error: validate(file, kind, info) };
}

export function contentTypeOf(file: File): string {
  if (file.type && file.type.length > 0) return file.type;
  if (/\.mov$/i.test(file.name)) return 'video/quicktime';
  if (/\.mp4|\.m4v$/i.test(file.name)) return 'video/mp4';
  if (/\.png$/i.test(file.name)) return 'image/png';
  if (/\.heic|\.heif$/i.test(file.name)) return 'image/heic';
  if (/\.jpe?g$/i.test(file.name)) return 'image/jpeg';
  return 'application/octet-stream';
}

export const ACCEPT_REACTION = 'video/*';
export const ACCEPT_ILLUSTRATION = `video/*,${PHOTO_TYPES.join(',')}`;
